import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useDebounce } from '../../../../../../hooks';
import * as service from '../services';
import { useSetupISACustomSegmentsStore } from '../SetupISACustomSegments.store';
import { ChainResponseDTO, SelectList } from '../types';

export interface ChainFilterBody {
  query: string;
  field: string;
  login: string | null;
  cliente: string | number | null;
  preco: 'PRECO_PAGO' | string;
}

const getStoredUser = () => {
  try {
    const usuario = localStorage.getItem('usuario');
    return usuario ? JSON.parse(usuario)?.email : null;
  } catch {
    return null;
  }
};

const getStoredClient = () => {
  try {
    const cliente = localStorage.getItem('cliente');
    return cliente ? JSON.parse(cliente)?.codigo : null;
  } catch {
    return null;
  }
};

export const useChainFilter = () => {
  const { chainFilterData, saveSegmentBody, setSetupISACustomSegmentsState } = useSetupISACustomSegmentsStore();

  const debouncedSearch = useDebounce();

  const updateSelection = (newValues: string[], newCache: SelectList[]) => {
    setSetupISACustomSegmentsState('chainFilterData', {
      ...chainFilterData,
      value: newValues,
      cache: newCache,
    });
    setSetupISACustomSegmentsState('saveSegmentBody', {
      ...saveSegmentBody,
      itens: newValues,
    });
  };

  const handleSuccess = (data: AxiosResponse) => {
    if (data.status === 200) {
      const newItems =
        data.data?.map((item: ChainResponseDTO) => ({
          value: item.key,
          label: item.key,
        })) || [];

      const { value: selectedValues, cache: selectedItems } = chainFilterData;

      const list = selectedValues.length > 0 ? [...selectedItems, ...newItems.filter((item: SelectList) => !selectedValues.includes(item.value))] : newItems;

      setSetupISACustomSegmentsState('chainFilterData', {
        ...chainFilterData,
        list,
      });
    }
  };

  const mutation = useMutation({
    mutationFn: (payload: ChainFilterBody) => service.getItensFiltros(payload),
    onSuccess: handleSuccess,
  });

  const handleSearchChainFilter = (value: string) => {
    const payload: ChainFilterBody = {
      query: value,
      field: 'rede',
      login: getStoredUser(),
      cliente: getStoredClient(),
      preco: 'PRECO_PAGO',
    };

    debouncedSearch(() => {
      mutation.mutateAsync(payload);
    });
  };

  const handleSelectChain = (item: SelectList) => {
    const { value: currentValues, cache: currentCache } = chainFilterData;
    const isSelected = currentValues.includes(item.value);

    let newValues: string[];
    let newCache: SelectList[];

    if (isSelected) {
      newValues = currentValues.filter(v => v !== item.value);
      newCache = currentCache.filter(c => c.value !== item.value);
    } else {
      newValues = [...currentValues, item.value];
      newCache = [...currentCache, item];
    }

    updateSelection(newValues, newCache);
  };

  const handleCleanChain = () => {
    updateSelection([], []);
  };

  return {
    isChainLoading: mutation.isPending,
    handleSearchChainFilter,
    handleCleanChain,
    handleSelectChain,
  };
};
